import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  ClockIcon,
  InfoIcon,
  PlusIcon,
  SunIcon,
  MonitorIcon,
  MoonIcon,
  BellIcon,
} from "./icons";
import { Tooltip } from "../../../components/Tooltip";
import { TariffLabel } from "../TariffLabel";
import { LogoIcon, MenuIcon } from "../Sidebar/icons";
import { HeaderProps, ThemeMode, ThemeSwitchProps } from "./types";

const THEMES: {
  mode: ThemeMode;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { mode: "light", Icon: SunIcon },
  { mode: "system", Icon: MonitorIcon },
  { mode: "dark", Icon: MoonIcon },
];

/** Light / system / dark: the header's three-way pill, also used in the phone menu. */
export const ThemeSwitch: React.FC<ThemeSwitchProps> = ({
  theme = "light",
  onThemeChange,
  className,
}) => (
  <div
    className={cs(styles.themeSwitch, className)}
    role="group"
    aria-label="Theme"
  >
    {THEMES.map(({ mode, Icon }) => (
      <button
        key={mode}
        type="button"
        className={cs(styles.switchItem, {
          [styles.active]: theme === mode,
        })}
        onClick={() => onThemeChange?.(mode)}
        aria-pressed={theme === mode}
        aria-label={mode}
      >
        <Icon />
      </button>
    ))}
  </div>
);

/**
 * Header — design-system top bar (Figma 4142:13187): the MCP widget on the
 * left, the wallet, theme switch, bell and avatar on the right.
 * Dumb component: all data and handlers come from props.
 */
export const Header: React.FC<HeaderProps> = ({
  time,
  timezone,
  infoTooltip,
  tariff,
  amount,
  onAddFund,
  theme = "light",
  onThemeChange,
  avatarSrc,
  onBell,
  onAvatar,
  walletSlot,
  bellSlot,
  avatarSlot,
  leadingSlot,
  mcpSlot,
  onMenu,
  menuOpen,
  className,
}) => {
  const phone = Boolean(onMenu);
  const wallet =
    walletSlot ??
    (tariff && amount && (
      <div className={styles.wallet}>
        <TariffLabel
          level={tariff.level ?? "trial"}
          label={tariff.label}
          meta={tariff.meta}
        />

        <span className={styles.amount}>
          <span className={styles.amountCurrency}>
            {amount.currency ?? "$"}
          </span>
          <span className={styles.amountWhole}>{amount.whole}</span>
          <span className={styles.amountFraction}>.{amount.fraction}</span>
        </span>

        <button
          type="button"
          className={styles.addFund}
          onClick={onAddFund}
          aria-label="Add funds"
        >
          <PlusIcon />
        </button>
      </div>
    ));

  return (
    <header className={cs(styles.header, { [styles.phone]: phone }, className)}>
      {phone && (
        <div className={styles.left}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={onMenu}
            aria-label="Open menu"
            aria-expanded={Boolean(menuOpen)}
          >
            <MenuIcon />
          </button>
          <LogoIcon className={styles.logo} />
        </div>
      )}

      {/* Left cluster: the clock (when the host wants one) and the MCP widget */}
      {!phone && (
        <div className={styles.left}>
          {time && (
            <div className={styles.time}>
              <div className={styles.timeInner}>
                <ClockIcon className={styles.icon20} />
                <p className={styles.timeText}>
                  {time} <span className={styles.timeZone}>{timezone}</span>
                </p>
              </div>
              {infoTooltip ? (
                <Tooltip title={infoTooltip} placement="right" appendToBody>
                  <span className={styles.infoIcon}>
                    <InfoIcon />
                  </span>
                </Tooltip>
              ) : (
                <InfoIcon className={styles.infoIcon} />
              )}
            </div>
          )}

          {mcpSlot}
        </div>
      )}

      {/* Right cluster */}
      <div className={styles.right}>
        {leadingSlot}

        {wallet}

        {!phone && <ThemeSwitch theme={theme} onThemeChange={onThemeChange} />}

        {/* Bell */}
        {bellSlot ?? (
          <button
            type="button"
            className={styles.bell}
            onClick={onBell}
            aria-label="Notifications"
          >
            <BellIcon />
          </button>
        )}

        {/* Avatar */}
        {avatarSlot ?? (
          <button
            type="button"
            className={styles.avatar}
            onClick={onAvatar}
            aria-label="Account"
            style={
              avatarSrc
                ? {
                    backgroundImage: `url(${avatarSrc})`,
                    backgroundSize: "cover",
                  }
                : undefined
            }
          />
        )}
      </div>
    </header>
  );
};

export default Header;
